/**
 * GLYDE Voice SDK - VoiceChat Component
 *
 * Full voice chat UI component with transcript display, speaking indicators,
 * and call controls.
 *
 * @example
 * <VoiceChat
 *   publishableKey="pk_..."
 *   contextType="screening"
 *   contextId="application-uuid"
 *   unityApiUrl="https://api.glydeunity.com"
 * />
 */

import React, { useRef, useEffect } from 'react';
import { useVoiceAgent } from '../hooks/useVoiceAgent';
import type { VoiceContextType, Transcript } from '../core/types';
import { MicIcon, PhoneOffIcon, VolumeXIcon, ActivityIcon, MessageIcon } from '../ui/icons';
import { componentStyles } from '../ui/styles';

/**
 * VoiceChat component props
 */
export interface VoiceChatProps {
  /** Application UUID (context ID for screening) */
  contextId: string;
  /** Publishable key for authentication */
  publishableKey?: string;
  /** API key for authentication */
  apiKey?: string;
  /** JWT auth token for GLYDEBuddy passthrough */
  authToken?: string;
  /** Unity API URL */
  unityApiUrl?: string;
  /** Voice context type */
  contextType?: VoiceContextType;
  /** Fixed height for the component */
  height?: string | number;
  /** Callback when transcript is added */
  onTranscript?: (text: string, role: 'user' | 'agent') => void;
  /** Callback when call ends */
  onCallEnd?: () => void;
  /** Callback when error occurs */
  onError?: (error: Error) => void;
  /** Callback to switch to text mode (shows "Prefer text?" link) */
  onSwitchToText?: () => void;
  /** When true, send full chat history for screening. Default false. */
  skipContinuityLimit?: boolean;
  /** Max last N messages when skipContinuityLimit is false (default 3; cdn-test use 5; CareerBuddy use 50). */
  limitToLast?: number;
}

/**
 * VoiceChat Component
 *
 * Provides a complete voice chat interface with:
 * - Start/stop call controls
 * - Real-time transcript display
 * - Speaking indicators (user and agent)
 * - Mute/unmute toggle
 */
export const VoiceChat: React.FC<VoiceChatProps> = ({
  contextId,
  publishableKey,
  apiKey,
  authToken,
  unityApiUrl = 'https://api.glydeunity.com',
  contextType = 'discovery',
  height = '600px',
  onTranscript,
  onCallEnd,
  onError,
  onSwitchToText,
  skipContinuityLimit,
  limitToLast,
}) => {
  const transcriptEndRef = useRef<HTMLDivElement>(null);

  const {
    status,
    transcripts,
    isUserSpeaking,
    isAgentSpeaking,
    isMuted,
    errorMessage,
    agentMetadata,
    languageConfig,
    activeLanguage,
    start,
    stop,
    toggleMute,
    setLanguage,
    clearTranscripts,
  } = useVoiceAgent({
    publishableKey,
    apiKey,
    authToken,
    contextType,
    contextId,
    unityBaseUrl: unityApiUrl,
    skipContinuityLimit,
    limitToLast,
  });

  // Auto-scroll transcripts
  useEffect(() => {
    transcriptEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [transcripts]);

  // Notify parent of transcripts
  useEffect(() => {
    if (onTranscript && transcripts.length > 0) {
      const latest = transcripts[transcripts.length - 1];
      onTranscript(latest.content, latest.role as 'user' | 'agent');
    }
  }, [transcripts, onTranscript]);

  // Notify parent of errors
  useEffect(() => {
    if (status === 'error' && errorMessage && onError) {
      onError(new Error(errorMessage));
    }
  }, [status, errorMessage, onError]);

  /**
   * Derive display name from server metadata or fall back to context type
   */
  const getContextTypeDisplay = () => {
    if (agentMetadata?.agent_name || agentMetadata?.company_name) {
      const parts = [agentMetadata.agent_name, agentMetadata.company_name].filter(Boolean);
      return parts.join(' - ');
    }
    switch (contextType) {
      case 'screening': return 'Screening Interview';
      case 'recruiter': return 'Recruiter Copilot';
      case 'custom': return 'Custom Agent';
      case 'phone': return 'Phone Agent';
      default: return 'Voice Interview';
    }
  };

  /**
   * Derive agent label for transcript bubbles
   */
  const getAgentLabel = () => {
    return agentMetadata?.agent_name || (contextType === 'recruiter' ? 'AI Copilot' : 'AI Recruiter');
  };

  /**
   * Handle start call
   */
  const handleStartCall = async () => {
    clearTranscripts();
    await start();
  };

  /**
   * Handle end call
   */
  const handleEndCall = () => {
    stop();
    onCallEnd?.();
  };

  return (
    <div style={{
      background: 'var(--glyde-background, white)',
      borderRadius: '12px',
      boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
      height: typeof height === 'number' ? `${height}px` : height,
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
    }}>
      {/* Header */}
      <div style={{
        padding: '10px 20px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        {status === 'active' && (
          <div style={{
            ...componentStyles.statusBadge,
            ...componentStyles.statusBadgeLive,
          }}>
            <ActivityIcon size={14} /> Live
          </div>
        )}
        {status === 'active' && languageConfig && languageConfig.voice_switching !== undefined && (
          <LanguageToggle
            languages={languageConfig.languages}
            activeLanguage={activeLanguage}
            onSelect={setLanguage}
          />
        )}
      </div>

      {/* Main Content */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {status === 'idle' || status === 'error' ? (
          /* Idle State */
          <div style={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '40px'
          }}>
            <div style={{
              width: '100px',
              height: '100px',
              borderRadius: '50%',
              background: 'var(--glyde-primary-light, #fdf4ff)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginBottom: '24px'
            }}>
              <MicIcon size={48} color="var(--glyde-primary, #c026d3)" />
            </div>
            <h3 style={{ marginBottom: '8px', color: 'var(--glyde-text, #1f2937)' }}>
              Ready for {getContextTypeDisplay()}?
            </h3>
            <button
              onClick={handleStartCall}
              style={{
                ...componentStyles.primaryButton,
                padding: '14px 36px',
                borderRadius: '30px',
                boxShadow: '0 4px 12px rgba(192, 38, 211, 0.3)',
              }}
            >
              Start {contextType === 'screening' ? 'Interview' : 'Call'}
            </button>
            {errorMessage && (
              <p style={{ color: 'var(--glyde-error, #ef4444)', marginTop: '16px', fontSize: '0.9rem' }}>
                {errorMessage}
              </p>
            )}
            <p style={{
              color: 'var(--glyde-text-secondary, #666)',
              marginBottom: '24px',
              textAlign: 'center',
              maxWidth: '280px'
            }}>
              {contextType === 'screening'
                ? 'The AI recruiter will speak with you. Make sure your microphone is ready.'
                : contextType === 'recruiter'
                ? 'The AI copilot will assist you with recruiting tasks.'
                : 'The AI agent will speak with you. Make sure your microphone is ready.'
              }
            </p>
            {/* Prefer text option */}
            {onSwitchToText && (
              <button
                onClick={onSwitchToText}
                style={{
                  marginTop: '20px',
                  padding: '8px 16px',
                  background: 'transparent',
                  border: 'none',
                  color: 'var(--glyde-secondary, #0284c7)',
                  fontSize: '0.9rem',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                }}
              >
                <MessageIcon size={16} color="var(--glyde-secondary, #0284c7)" />
                Prefer to type? Switch to text chat
              </button>
            )}
          </div>
        ) : (
          /* Active/Connecting State */
          <>
            {/* Transcript Area */}
            <div style={{
              flex: 1,
              overflowY: 'auto',
              padding: '16px',
              background: 'var(--glyde-surface, #f9fafb)'
            }}>
              {status === 'connecting' && (
                <div style={{ textAlign: 'center', color: 'var(--glyde-text-secondary, #6b7280)', padding: '40px' }}>
                  <div className="glyde-spinner" style={{ margin: '0 auto 12px' }} />
                  Connecting to AI Agent...
                </div>
              )}

              {transcripts.length === 0 && status === 'active' && (
                <div style={{ textAlign: 'center', color: 'var(--glyde-text-muted, #9ca3af)', padding: '40px' }}>
                  <MessageIcon size={32} color="var(--glyde-text-muted, #9ca3af)" />
                  <p style={{ marginTop: '8px' }}>Start speaking - the AI will respond</p>
                </div>
              )}

              {transcripts.map((t, i) => (
                <TranscriptMessage key={i} transcript={t} agentLabel={getAgentLabel()} />
              ))}
              <div ref={transcriptEndRef} />
            </div>

            {/* Status & Controls */}
            <div style={{
              padding: '16px',
              borderTop: '1px solid var(--glyde-border, #e5e7eb)',
              background: 'var(--glyde-background, white)'
            }}>
              {/* Speaking Indicators */}
              <SpeakingIndicators
                isUserSpeaking={isUserSpeaking}
                isAgentSpeaking={isAgentSpeaking}
              />

              {/* Control Buttons */}
              <div style={{ display: 'flex', justifyContent: 'center', gap: '12px', marginTop: '16px' }}>
                <button
                  onClick={toggleMute}
                  style={{
                    ...componentStyles.iconButton,
                    background: isMuted ? 'var(--glyde-error-light, #fecaca)' : 'var(--glyde-secondary-light, #e0f2fe)',
                  }}
                  title={isMuted ? 'Unmute' : 'Mute'}
                >
                  {isMuted
                    ? <VolumeXIcon size={20} color="var(--glyde-error, #dc2626)" />
                    : <MicIcon size={20} color="var(--glyde-secondary, #0ea5e9)" />
                  }
                </button>
                <button
                  onClick={handleEndCall}
                  style={componentStyles.dangerButton}
                >
                  <PhoneOffIcon size={18} color="white" /> End Call
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Inline styles for animations */}
      <style>{`
        @keyframes glyde-pulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.5; transform: scale(1.2); }
        }
      `}</style>
    </div>
  );
};

/**
 * Language toggle component (Auto / EN / ES ...).
 *
 * Rendered when the server exposes a language_config. 'Auto' lets the SDK
 * follow the detected language of each user turn; selecting a language pins
 * the agent's voice to that language.
 */
const LanguageToggle: React.FC<{
  languages: string[];
  activeLanguage: string | null;
  onSelect: (mode: 'auto' | string) => void;
}> = ({ languages, activeLanguage, onSelect }) => {
  const autoActive = activeLanguage === null || activeLanguage === languages[0];

  const segmentStyle = (selected: boolean): React.CSSProperties => ({
    padding: '4px 10px',
    fontSize: '0.75rem',
    fontWeight: selected ? 600 : 400,
    borderRadius: '999px',
    border: 'none',
    cursor: 'pointer',
    background: selected ? 'var(--glyde-primary, #c026d3)' : 'transparent',
    color: selected ? 'white' : 'var(--glyde-text-secondary, #666)',
  });

  return (
    <div
      role="group"
      aria-label="Agent language"
      style={{
        display: 'inline-flex',
        gap: '2px',
        padding: '2px',
        borderRadius: '999px',
        background: 'var(--glyde-surface, #f3f4f6)',
      }}
    >
      <button style={segmentStyle(autoActive)} onClick={() => onSelect('auto')} title="Follow detected language">
        Auto
      </button>
      {languages.map((lang) => (
        <button
          key={lang}
          style={segmentStyle(activeLanguage === lang)}
          onClick={() => onSelect(lang)}
          title={`Pin agent voice to ${lang.toUpperCase()}`}
        >
          {lang.toUpperCase()}
        </button>
      ))}
    </div>
  );
};

/**
 * Transcript message component
 */
const TranscriptMessage: React.FC<{ transcript: Transcript; agentLabel: string }> = ({
  transcript,
  agentLabel
}) => {
  const isUser = transcript.role === 'user';

  return (
    <div style={{
      marginBottom: '12px',
      display: 'flex',
      justifyContent: isUser ? 'flex-end' : 'flex-start'
    }}>
      <div style={{
        ...componentStyles.messageBubble,
        ...(isUser ? componentStyles.messageBubbleUser : componentStyles.messageBubbleAgent),
      }}>
        <div style={{ fontSize: '0.75rem', opacity: 0.7, marginBottom: '4px' }}>
          {isUser ? 'You' : agentLabel}
        </div>
        {transcript.content}
      </div>
    </div>
  );
};

/**
 * Speaking indicators component
 */
const SpeakingIndicators: React.FC<{ isUserSpeaking: boolean; isAgentSpeaking: boolean }> = ({
  isUserSpeaking,
  isAgentSpeaking,
}) => (
  <div style={{
    display: 'flex',
    justifyContent: 'center',
    gap: '20px',
  }}>
    <div style={{
      ...componentStyles.speakingIndicator,
      color: isUserSpeaking ? 'var(--glyde-primary, #c026d3)' : 'var(--glyde-text-muted, #9ca3af)'
    }}>
      <div style={{
        ...componentStyles.speakingDot,
        background: isUserSpeaking ? 'var(--glyde-primary, #c026d3)' : 'var(--glyde-border, #d1d5db)',
        animation: isUserSpeaking ? 'glyde-pulse 1s infinite' : 'none'
      }} />
      You
    </div>
    <div style={{
      ...componentStyles.speakingIndicator,
      color: isAgentSpeaking ? 'var(--glyde-success, #059669)' : 'var(--glyde-text-muted, #9ca3af)'
    }}>
      <div style={{
        ...componentStyles.speakingDot,
        background: isAgentSpeaking ? 'var(--glyde-success, #059669)' : 'var(--glyde-border, #d1d5db)',
        animation: isAgentSpeaking ? 'glyde-pulse 1s infinite' : 'none'
      }} />
      AI Agent
    </div>
  </div>
);

export default VoiceChat;
